import {BaseCard} from "../../Components/Cards/BaseCard";
import {useEffect, useState} from "react";
import * as yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {DateTimePickerInput} from "../../Components/Form/DateTimePickerInput";
import * as React from "react";
import {SelectInput} from "../../Components/Form/SelectInput";
import {useBoundStore} from "../../Store/store";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {ShiftsTable} from "../../Components/Tables/TableCards/ShiftsTable";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDownload} from "@fortawesome/free-solid-svg-icons";
import {BlackButton} from "../../Components/Buttons/BlackButton";
import {MonthlyInvoicedChart} from "../../Components/Charts/MonthlyInvoicedChart";
import {TextAreaInput} from "../../Components/Form/TextAreaInput";
import {PrimaryButton} from "../../Components/Buttons/PrimaryButton";
import {BaseClient} from "../../Api/HttpClient";
import toast from "react-hot-toast";
import {AcceptConfirmDialog} from "../../Components/Dialogs/Confirmation/AcceptConfirmDialog";
import {ConfirmActionDialog} from "../../Components/Dialogs/Confirmation/ConfirmActionDialog";

export const BulkMail = () => {
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [openConfirm, setOpenConfirm] = React.useState(false);

    const roleFilters = [
        {id: 'all', value: 'all', label: t('bulk_mail.role_everyone')},
        {id: 'customer', value: 'customer', label: t('bulk_mail.role_customers')},
        {id: 'freelancer', value: 'freelancer', label: t('bulk_mail.role_freelancers')},
    ];

    const jobFunctions = [
        {value: 'A1', label: t('functions.A1'), id: 0},
        {value: 'A2', label: t('functions.A2'), id: 1},
        {value: 'A0', label: t('functions.A0'), id: 2},
        {value: 'A3', label: t('functions.A3'), id: 3}
    ];

    const statutenOptions = [
        {value: 'freelance', label: t('statuten.freelance'), id: 0},
        {value: 'interim', label: t('statuten.interim'), id: 1},
    ];

    const schema = yup.object({
        role_filter: yup.mixed().required(),
        function_filter: yup.mixed().nullable(),
        statuut_filter: yup.array().of(yup.string()).nullable(),
        message: yup.string().required(t('bulk_mail.message_required')).min(10, t('bulk_mail.message_min')),
        subject: yup.string().required(t('bulk_mail.subject_required')).min(5, t('bulk_mail.subject_min')).max(950, t('bulk_mail.subject_max')),
    }).required();

    const {
        register,
        handleSubmit,
        control,
        setValue,
        getValues,
        reset,
        watch,
        formState:{errors}
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            role_filter: 'all',
            function_filter: null,
            statuut_filter: [],
            subject: '',
            message: '',
        }
    });

    const roleFilter = watch('role_filter');

    const onSubmit = async (data) => {
        setOpenConfirm(true);
    }

    const submitMail = async (data) => {
        setIsLoading(true);
        await BaseClient().post('/bulk-mail', {
            role_filter: getValues('role_filter'),
            function_filter: getValues('function_filter'),
            statuut_filter: getValues('statuut_filter'),
            subject: getValues('subject'),
            message: getValues('message')
        }).then(response => {
            if (response.status === 200) {
                toast.success(t('bulk_mail.toast_sent_success'));
                reset({
                    role_filter: 'all',
                    function_filter: null,
                    statuut_filter: [],
                    message: '',
                    subject: '',
                });
                setIsLoading(false);
            } else {
                toast.error(response.data);
                setIsLoading(false);
            }
        }).catch(error => {
            console.error(error);
            toast.error(t('bulk_mail.toast_sent_error'));
            setIsLoading(false);
        });
    }

    return (
        <div className={'grid grid-cols-1 gap-4'}>
            <BaseCard>
                <div className={'grid grid-cols-1 md:grid-cols-3 gap-4'}>
                    <SelectInput
                        name={'role_filter'}
                        options={roleFilters}
                        control={control}
                        value={getValues('role_filter') ?? []}
                        setValue={setValue}
                        placeholder={t('bulk_mail.audience_placeholder')}
                        translationIndex={'bulk_mail.audience_label'}
                    />

                    {
                        roleFilter === 'freelancer' && (
                            <SelectInput
                                name={'statuut_filter'}
                                register={register}
                                control={control}
                                setValue={setValue}
                                translationIndex={'general.fields.main_statuut'}
                                options={statutenOptions}
                                errors={errors}
                                multiple={true}
                            />
                        )
                    }

                    {
                        roleFilter === 'freelancer' && (
                            <SelectInput
                                name={'function_filter'}
                                register={register}
                                control={control}
                                setValue={setValue}
                                translationIndex={'general.fields.function'}
                                options={jobFunctions}
                                errors={errors}
                                multiple={true}
                            />
                        )
                    }
                </div>
            </BaseCard>

            <BaseCard>
                <h2 className={'text-lg text-gray-700 uppercase mb-4 mt-2'}>{t('bulk_mail.content_heading')}</h2>

                <form onSubmit={handleSubmit(onSubmit)}>
                    <TextAreaInput
                        register={register}
                        name={'subject'}
                        translationIndex={'bulk_mail.subject_placeholder'}
                        rows={2}
                        errors={errors}
                    />

                    <TextAreaInput
                        register={register}
                        name={'message'}
                        translationIndex={'bulk_mail.message_placeholder'}
                        errors={errors}
                    />

                    <div className={'mt-4'}>
                        <PrimaryButton type="submit" disabled={isLoading} className={'w-full'}>
                            {isLoading ? t('bulk_mail.sending') : t('bulk_mail.send_button')}
                        </PrimaryButton>
                    </div>
                </form>

            </BaseCard>

            <ConfirmActionDialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                onConfirm={async () => {
                    await submitMail();
                    setOpenConfirm(false);
                }}
            />
        </div>
    )
}